import { useQuery } from '@tanstack/react-query';
import { MessageSquare } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ConversationList } from '@up2d8/contracts';
import { cn } from '@up2d8/ui';
import { useSession } from '../../../shared/session';
import { DeskConversationList } from './desk-conversation-list';
import { DeskNewConversation } from './desk-new-conversation';

/**
 * Het berichtenscherm op een bureauscherm: drie panelen naast elkaar (replica-spec §2).
 *
 * De pagina zelf scrolt niet — dat zet `DeskShell` met `fill`, en het is een afwijking van elke
 * andere pagina in de app. Alleen de lijsten binnen de panelen scrollen.
 *
 * Elk paneel krijgt `min-h-0` en `overflow-hidden`: zonder die twee kruipt een scrollend kind
 * buiten zijn kaart, en dan scrolt de hele pagina alsnog.
 *
 * Paneel 2 en 3 komen in BER-6 en BER-7. Tot dan staat er wat er hoort te staan wanneer er geen
 * gesprek gekozen is — geen stuk bouwsteiger, want dit scherm draait op de testomgeving.
 */
export interface DeskMessagesPageProps {
  /** Het geopende gesprek, als er een gekozen is. */
  activeId?: string | null;
  /** Paneel 2 — het gesprek (BER-6). */
  conversation?: ReactNode;
  /** Paneel 3 — groepsinfo of collega-info (BER-7). */
  info?: ReactNode;
}

export function DeskMessagesPage({ activeId = null, conversation, info }: DeskMessagesPageProps) {
  const { t } = useTranslation();
  const { api } = useSession();
  const [newOpen, setNewOpen] = useState(false);

  const conversations = useQuery({
    queryKey: ['conversations'],
    queryFn: async (): Promise<ConversationList> => {
      const { data, error } = await api.GET('/api/v1/conversations');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as ConversationList;
    },
  });

  return (
    <div
      data-testid="chat-layout"
      className={cn(
        // §7: tot 1100 px wordt paneel 1 280 breed en krimpt de tussenruimte naar 16.
        'mx-auto grid h-full w-full max-w-[1600px] gap-4 wide:gap-5',
        // §2 schrijft drie kolommen voor, en zo staat het er zodra er een gesprek open is. Zonder
        // gesprek is er ook geen info te tonen; dan zou de derde kolom een lege baan van 300 px
        // zijn naast een paneel dat er niet tot aan de rand doorloopt.
        // §7: het infopaneel bestaat pas vanaf 1400 px. Daaronder is er geen ruimte voor drie
        // kolommen zonder dat het gesprek zelf te smal wordt om te lezen.
        info
          ? 'grid-cols-[280px_minmax(0,1fr)] wide:grid-cols-[320px_minmax(0,1fr)] full:grid-cols-[320px_minmax(0,1fr)_300px]'
          : 'grid-cols-[280px_minmax(0,1fr)] wide:grid-cols-[320px_minmax(0,1fr)]',
      )}
    >
      <DeskConversationList
        data={conversations.data}
        loading={conversations.isPending}
        activeId={activeId}
        onNew={() => setNewOpen(true)}
      />

      {conversation ?? (
        <EmptyPanel
          title={t('messaging.pickConversation')}
          hint={t('messaging.pickConversationHint')}
        />
      )}

      {/* `hidden full:flex` en niet weglaten: wie het paneel open had en zijn venster versmalt,
          hoort die keuze terug te vinden zodra het weer breed genoeg is. */}
      {info ? <div className="hidden min-h-0 min-w-0 full:flex full:flex-col">{info}</div> : null}

      <DeskNewConversation open={newOpen} onClose={() => setNewOpen(false)} />
    </div>
  );
}

function EmptyPanel({ title, hint }: { title: string; hint: string }) {
  return (
    <div className="flex min-h-0 min-w-0 flex-col items-center justify-center overflow-hidden rounded-card border border-border-light bg-surface p-element text-center shadow-sm">
      <MessageSquare aria-hidden className="mb-3 h-8 w-8 text-text-muted" />
      <p className="font-heading text-content font-semibold text-text-primary">{title}</p>
      <p className="mt-1 max-w-xs text-small text-text-muted">{hint}</p>
    </div>
  );
}
